/**
 * Internal Href
 *
 * Classifies hrefs for in-app navigation.
 *
 * Primary responsibilities:
 * - Convert relative or same-origin absolute hrefs to in-app paths
 * - Detect genuinely external http(s) hrefs
 *
 * Dependencies:
 * - Browser URL / window.location (client only)
 */

// --------------

export const toInAppHref = (href: string | null | undefined): string | null => {
  const trimmed = href?.trim()

  if (!trimmed) {
    return null
  }

  // Root-relative paths (but not protocol-relative //host/path) are in-app.
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
    return trimmed
  }

  if (!/^https?:\/\//i.test(trimmed)) {
    return null
  }

  if (typeof window === 'undefined') {
    return null
  }

  try {
    const url = new URL(trimmed)

    if (url.origin !== window.location.origin) {
      return null
    }

    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return null
  }
}

// --------------

export const isExternalHttpHref = (href: string | null | undefined): boolean => {
  const trimmed = href?.trim()

  if (!trimmed || !/^https?:\/\//i.test(trimmed)) {
    return false
  }

  return toInAppHref(trimmed) === null
}
